import type { DateInvite } from '@/api/types';
import type { LateNotice, LateView } from '@/features/notify/api';

/**
 * Running late and "Can't make it" (SPEC_V9 §D4). The server decides the
 * window (T−30…T+30) and the cap (2 per person per date); these helpers only
 * word and pick what to show.
 */

export const LATE_MINUTES = [5, 10, 15] as const;

/** First-person labels for the preset reasons (the other person sees a kind third-person line). */
export const REASON_LABELS: Record<string, string> = {
  something_came_up: 'Something came up',
  running_behind: 'My day ran long',
  not_feeling_well: 'I’m not feeling well',
  need_another_day: 'Today isn’t working out',
  connection: 'My connection isn’t great',
};

/** "Maya is running 10 min late 🙏 — hang tight" (the same words as the WhatsApp/push summary). */
export function lateLine(name: string, n: Pick<LateNotice, 'minutes'>): string {
  return `${name} is running ${n.minutes ?? 5} min late 🙏 — hang tight`;
}

/** The other person's most recent notice for this date time, if any. */
export function partnerNotice(view: LateView | undefined, myId: string): LateNotice | null {
  const theirs = (view?.notices ?? []).filter((n) => n.user_id !== myId);
  return theirs.at(-1) ?? null;
}

/** My most recent running-late notice. */
export function myLate(view: LateView | undefined, myId: string): LateNotice | null {
  return (view?.notices ?? []).filter((n) => n.user_id === myId && n.kind === 'late').at(-1) ?? null;
}

/** Whether I already said I can't make it. */
export function iCant(view: LateView | undefined, myId: string): boolean {
  return (view?.notices ?? []).some((n) => n.user_id === myId && n.kind === 'cant');
}

/** A date these controls apply to: a time, a guest, and no call yet. */
export function lateEligible(inv: DateInvite): boolean {
  return (inv.status === 'scheduled' || inv.status === 'accepted') && !!inv.starts_at && !!inv.guest && !inv.session_id;
}

/** "Can't make it" stays open until 30 minutes after the start. */
export function canCancelNow(inv: DateInvite, now = Date.now()): boolean {
  return lateEligible(inv) && now <= Date.parse(inv.starts_at!) + 30 * 60_000;
}

/** "8:40 PM" for an ETA. */
export function etaLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
